
import React, { useState } from 'react';
import { useActivityManagement } from '@/hooks/useActivityManagement';
import { useDragAndDrop } from '@/hooks/useDragAndDrop';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Plus, Clock } from 'lucide-react';
import { ActivityDialog } from './ActivityDialog';
import { ActivityItem } from './ActivityItem';
import { CategoryPills } from './CategoryPills';
import { VideoModal } from '@/components/VideoModal';
import { Program, Activity } from '@/types/program';

interface ActivityManagerProps {
  program: Program;
  onBack: () => void;
}

export const ActivityManager = ({ program, onBack }: ActivityManagerProps) => {
  const { session } = useAuth();
  const { activities, loading, totalDuration, createActivity, updateActivity, deleteActivity, reorderActivities } = useActivityManagement(program.id);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedVideoActivity, setSelectedVideoActivity] = useState<Activity | null>(null);

  const {
    draggedIndex,
    dragOverIndex,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop
  } = useDragAndDrop(activities, reorderActivities);

  const handleCreateActivity = async (activityData: Omit<Activity, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      await createActivity({
        ...activityData,
        program_id: program.id,
        order_index: activities.length
      });
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Failed to create activity:', error);
    }
  };

  const handleEditActivity = (activity: Activity) => {
    setEditingActivity(activity);
    setIsDialogOpen(true);
  };

  const handleUpdateActivity = async (activityData: Omit<Activity, 'id' | 'created_at' | 'updated_at'>) => {
    if (!editingActivity) return;
    
    try {
      await updateActivity(editingActivity.id, activityData);
      setIsDialogOpen(false);
      setEditingActivity(null);
    } catch (error) {
      console.error('Failed to update activity:', error);
    }
  };

  const handleDeleteActivity = async (activityId: string) => {
    if (!confirm('Are you sure you want to delete this activity?')) {
      return;
    }

    try {
      await deleteActivity(activityId);
    } catch (error) {
      console.error('Failed to delete activity:', error);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingActivity(null);
  };

  const handleWatchVideo = (activity: Activity) => {
    setSelectedVideoActivity(activity);
    setVideoModalOpen(true);
  };

  const handleCloseVideoModal = () => {
    setVideoModalOpen(false);
    setSelectedVideoActivity(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Programs
          </Button>
          <div>
            <h3 className="text-lg font-semibold">{program.title}</h3>
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Total Duration: {totalDuration} minutes
            </p>
          </div>
        </div>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Activity
        </Button>
      </div>


      <Card>
        <CardHeader>
          <CardTitle>Activities</CardTitle>
          <CardDescription>
            Manage the activities in this training program. Drag activities to reorder them.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-4">Loading activities...</div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8">
              <h4 className="text-lg font-semibold mb-2">No Activities</h4>
              <p className="text-muted-foreground mb-4">
                Add activities to build your training program.
              </p>
            </div>
          ) : (
            <>
              <CategoryPills activities={activities} />

              <div className="space-y-4">
                {activities.map((activity, index) => (
                  <ActivityItem
                    key={activity.id}
                    activity={activity}
                    index={index}
                    isDragged={draggedIndex === index}
                    isDraggedOver={dragOverIndex === index}
                    onEdit={handleEditActivity}
                    onDelete={handleDeleteActivity}
                    onWatchVideo={handleWatchVideo}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ActivityDialog
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        onSubmit={editingActivity ? handleUpdateActivity : handleCreateActivity}
        activity={editingActivity}
      />

      {selectedVideoActivity && (
        <VideoModal
          isOpen={videoModalOpen}
          onClose={handleCloseVideoModal}
          activity={selectedVideoActivity}
        />
      )}
    </div>
  );
};
